import * as React from 'react';
import propz from 'propz';
import { ClubProgrammeBookingItem, ClubProgrammeStudentConflict } from 'Src/helpers/service/admin/clubProgrammes';
import { fmtAcceptedAt } from '../helpers/clubProgrammeBookingsBoard';

type ProgrammeAnswerField = {
  heading: string;
  type: 'BOOLEAN' | 'ENUM' | 'NUMBER' | 'STRING';
  isRequired?: boolean;
  isDefault?: boolean;
  enumOptions?: string[];
  value?: any;
};

type Props = {
  item: ClubProgrammeBookingItem;
  studentConflict?: ClubProgrammeStudentConflict | null;
  renderItemActionButtons: (item: ClubProgrammeBookingItem) => React.ReactNode;
  onOpenAnswers: (title: string, item: ClubProgrammeBookingItem) => void;
  onOpenConflict: (conflict: ClubProgrammeStudentConflict) => void;
  allocationActionsLocked?: boolean;
};

export const ClubProgrammeBookingItemCard: React.FC<Props> = ({
  item,
  studentConflict,
  renderItemActionButtons,
  onOpenAnswers,
  onOpenConflict,
  allocationActionsLocked
}) => {
  const st = String(item.invitationStatus || '').toUpperCase();
  const studentName = String(item.student?.name || 'Student');
  const pr = item.priority;
  const acceptedAt = item.acceptedAt ?? null;
  const fields: ProgrammeAnswerField[] = Array.isArray(item.fields) ? item.fields : [];

  const allocationOutcome = String(item.schoolAllocation?.allocationOutcome || '').toUpperCase();
  const isSelected = !!item.schoolAllocation?.isSelected;
  const isConfirmed = !!item.schoolAllocation?.isConfirmed;
  const isWaitingListOutcome = allocationOutcome === 'WAITING_LIST';

  const isThisConfirmedClub = !!item.uiState?.isThisConfirmedClub;
  const isThisConfirmedWaitingList = !!item.uiState?.isThisConfirmedWaitingList;

  const isPendingRemove = !!item.uiState?.isPendingRemove;
  const pendingRemoveOutcome = String(item.uiState?.currentOutcome || '').toUpperCase();

  const isPendingRemoveClub = isPendingRemove && pendingRemoveOutcome === 'CLUB';

  const isPendingRemoveWaitingList = isPendingRemove && pendingRemoveOutcome === 'WAITING_LIST';

  const isConflicting = !isThisConfirmedClub && !!studentConflict;

  const showSelectedBadge = isSelected && !isConfirmed;

  const statusBadge =
    st === 'ACCEPTED'
      ? 'pbBadge--success'
      : st === 'PROGRAMME_DRAFT'
      ? 'pbBadge--info'
      : st.startsWith('WAITING_LIST')
      ? 'pbBadge--warning'
      : 'pbBadge--muted';

  const statusLabel =
    st === 'ACCEPTED'
      ? 'Accepted'
      : st === 'PROGRAMME_DRAFT'
      ? 'Draft'
      : st.startsWith('WAITING_LIST')
      ? 'Waiting list'
      : st
          .toLowerCase()
          .split('_')
          .filter(Boolean)
          .map(part => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' ');

  return (
    <div
      className={[
        'cpBookingsCard',
        isConflicting ? 'cpBookingsCard--conflict' : '',
        isConfirmed
          ? isWaitingListOutcome
            ? 'cpBookingsCard--confirmedWaiting'
            : 'cpBookingsCard--confirmedClub'
          : isSelected
          ? isWaitingListOutcome
            ? 'cpBookingsCard--selectedWaiting'
            : 'cpBookingsCard--selectedClub'
          : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="cpBookingsCardTop">
        <div className="cpBookingsCardMain">
          <div className="cpBookingsStudentName">{studentName}</div>

          <div className="cpBookingsMeta">
            <div>
              Accepted: <span className="cpBookingsMetaStrong">{fmtAcceptedAt(acceptedAt)}</span>
            </div>

            {item.acceptedByName ? (
              <div>
                By: <span className="cpBookingsMetaStrong">{String(item.acceptedByName)}</span>
                {item.acceptedByRole ? ` (${String(item.acceptedByRole)})` : ''}
              </div>
            ) : null}
          </div>
        </div>

        <div className="cpBookingsSide">
          <span className={`pbBadge ${statusBadge}`}>{statusLabel}</span>

          {typeof pr === 'number' ? <span className="pbBadge pbBadge--priority">P{pr}</span> : null}

          {showSelectedBadge ? (
            <span className={`pbBadge ${isWaitingListOutcome ? 'pbBadge--warning' : 'pbBadge--info'}`}>
              {isWaitingListOutcome ? 'Selected: WL' : 'Selected: Club'}
            </span>
          ) : null}

          {isThisConfirmedClub ? <span className="pbBadge pbBadge--success">In club</span> : null}

          {isThisConfirmedWaitingList ? <span className="pbBadge pbBadge--warning">In waiting list</span> : null}

          {isPendingRemoveClub ? <span className="pbBadge pbBadge--danger">Pending remove</span> : null}

          {isPendingRemoveWaitingList ? <span className="pbBadge pbBadge--danger">Pending WL remove</span> : null}
        </div>
      </div>

      <div className="cpBookingsQuickActions">
        <button
          type="button"
          className="cpIconBtn"
          onClick={() => onOpenAnswers(studentName, item)}
          disabled={!item.formDefinition && (!fields || !fields.length)}
          title={item.formDefinition || (fields && fields.length) ? 'View answers' : 'No answers'}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M7 3.5h7l4 4V20H7zM14 3.5V8h4M10 12h5M10 15.5h5" />
          </svg>
        </button>

        {isConflicting && studentConflict ? (
          <button
            type="button"
            className="cpIconBtn cpIconBtn--danger"
            onClick={() => onOpenConflict(studentConflict)}
            title="View conflict"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m12 3 9 17H3zM12 9v5M12 17.5h.01" />
            </svg>
          </button>
        ) : null}
      </div>

      <div className="cpBookingsActions">{renderItemActionButtons(item)}</div>

      {allocationActionsLocked ? (
        <div className="cpBookingsMutedNote">Allocation actions are locked because this programme is completed.</div>
      ) : null}

      {isConflicting ? (
        <div className="cpBookingsConflictNote">Conflict: this student appears in multiple clubs for this day.</div>
      ) : null}

      {item.uiState?.isDisabledBecauseAllocatedElsewhere && item.uiState?.disabledReason ? (
        <div className="cpBookingsMutedNote">{String(item.uiState.disabledReason)}</div>
      ) : null}
    </div>
  );
};

ClubProgrammeBookingItemCard.displayName = 'ClubProgrammeBookingItemCard';
